import { useState } from "react";
import { Box, Typography, Tabs, Tab, Alert, Snackbar, CircularProgress } from "@mui/material";
import CredentialTable from "./CredentialTable";
import CredentialFormDialog from "./CredentialFormDialog";
import { CATEGORIES, GROUPS } from "./constants";
import useAgentCredentials from "./hooks/useAgentCredentials";

const primaryBlue = "#1D54A2";

const AgentCredentialVault = () => {
	const { credentials, loading, error, updateCredential } = useAgentCredentials();
	const [activeCategory, setActiveCategory] = useState(CATEGORIES[0]);
	const [editingCredential, setEditingCredential] = useState(null);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

	const visibleCredentials = credentials.filter((c) => c.category === activeCategory);

	const closeDialog = () => setEditingCredential(null);

	const handleSubmit = async (payload) => {
		const result = await updateCredential(editingCredential.id, payload);

		if (!result.success) {
			setSnackbar({ open: true, message: result.error || "Failed to save credential.", severity: "error" });
			return;
		}

		closeDialog();
		setSnackbar({
			open: true,
			message: result.data?.notified
				? "Saved. Your admin has been notified."
				: "Saved. No admin notification email is set up yet.",
			severity: "success",
		});
	};

	return (
		<Box>
			<Typography variant="h5" fontWeight={600} mb={0.5}>
				Credential Vault
			</Typography>
			<Typography variant="body2" color="text.secondary" mb={2}>
				Carrier login credentials your administrator has set up for you.
			</Typography>

			<Alert severity="info" variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
				Changes take effect immediately. Your admin is notified by email whenever you update a credential.
			</Alert>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<Tabs
				value={activeCategory}
				onChange={(_e, value) => setActiveCategory(value)}
				sx={{
					mb: 2,
					borderBottom: "1px solid",
					borderColor: "divider",
					minHeight: 0,
					"& .MuiTab-root": {
						fontWeight: 600,
						textTransform: "none !important",
						color: "#4A6A8A !important",
						backgroundColor: "transparent !important",
						border: "none !important",
						minHeight: 0,
						py: 1.5,
						"&:hover": {
							color: `${primaryBlue} !important`,
							backgroundColor: "#EBF5FF !important",
						},
						"&:focus-visible": {
							outline: `2px solid ${primaryBlue}`,
							outlineOffset: -2,
						},
					},
					"& .Mui-selected": {
						color: `${primaryBlue} !important`,
						backgroundColor: "transparent !important",
					},
					"& .MuiTabs-indicator": { backgroundColor: `${primaryBlue} !important`, height: 2 },
				}}
			>
				{CATEGORIES.map((category) => (
					<Tab key={category} value={category} label={category} />
				))}
			</Tabs>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress size={26} sx={{ color: primaryBlue }} />
				</Box>
			) : (
				GROUPS.map((group) => (
					<CredentialTable
						key={group}
						title={group}
						credentials={visibleCredentials.filter((c) => c.group === group)}
						onEdit={setEditingCredential}
					/>
				))
			)}

			<CredentialFormDialog open={!!editingCredential} onClose={closeDialog} onSubmit={handleSubmit} initialValues={editingCredential} />

			<Snackbar
				open={snackbar.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity={snackbar.severity} sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default AgentCredentialVault;
